import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import UserAPI from 'api/UserAPI';
import { ConfigEntryType, ConfigFieldEntryType } from 'config/config';
import { UserType } from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { updateUsersQueryCache } from '@buildwise/libs/people';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { CustomFieldValueType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { UserData } from '@buildwise/pages/admin/userAdministration/CompanyUsers';
import { getPredefinedFieldValidation } from '@buildwise/libs/people/helpers/user-form.helpers';

export type ModifyUserFormSchema = {
  canAddProjects: boolean;
  firstname: string;
  lastname: string;
  email: string;
  primaryCompanyName: string;
  role: string;
  active: string;
  canEndorseFlag: boolean;
  canSuggestRevisiontToPostsFlag: boolean;
  canMovePostsToTopFlag: boolean;
  canCreatePlaylistFlag: boolean;
  canInviteOthersFlag: boolean;
};

export const editUserSchema = (customFields: ConfigEntryType[]) =>
  Yup.object().shape({
    ...getCustomFieldsValidationSchema({
      fields: customFields,
      getPredefinedFieldValidation: ({ field }) => getPredefinedFieldValidation({ field }),
    }),
    firstname: Yup.string()
      .required('Required field')
      .min(2, 'The first name must contain at least 2 characters')
      .nullable(),
    lastname: Yup.string()
      .required('Required field')
      .min(1, 'The last name must be at least one character')
      .nullable(),
    email: Yup.string().required('Required field'),
    // primaryCompanyName: Yup.string()
    //   .required('Required field')
    //   .min(2, 'Company name must contain at least 2 characters'),
    role: Yup.string().required('Required field'),
    active: Yup.string().required('Required field'),
    canEndorseFlag: Yup.boolean(),
    canSuggestRevisiontToPostsFlag: Yup.boolean(),
    canMovePostsToTopFlag: Yup.boolean(),
    canCreatePlaylistFlag: Yup.boolean(),
    canInviteOthersFlag: Yup.boolean(),
    addVoicePost: Yup.boolean(),
    articlesLLMSearch: Yup.boolean(),
  });

export function useUserForm({
  user,
  editUser,
  setOpen,
  companyId = '',
}: {
  user: UserData;
  editUser: UserType | undefined;
  setOpen: Dispatch<SetStateAction<boolean>>;
  companyId?: string;
}) {
  const { activeUniqueEntries: customInputFields, isFetched: isCustomFieldsFetched } =
    useConfigQuery({
      ...(companyId && { companyId }),
      configName: CONFIGS.USER_INPUT_FIELDS.configName,
    });
  const getDefaultValues = () => {
    return {
      ...getCustomFieldsDefaultValues({ data: editUser, fields: customInputFields }),
      firstname: user.firstName || '',
      lastname: user.lastName || '',
      email: user.userName || '',
      primaryCompanyName: user.primaryCompanyName || '',
      role: user.userRole || '',
      active: user.status || '',
      canEndorseFlag: user?.canEndorseFlag || false,
      canSuggestRevisiontToPostsFlag: user?.canSuggestRevisiontToPostsFlag || false,
      canMovePostsToTopFlag: user?.canMovePostsToTopFlag || false,
      canCreatePlaylistFlag: user?.canCreatePlaylistFlag || false,
      canInviteOthersFlag: user?.canInviteOthersFlag || false,
      canAddProjects: user?.canAddProjects || false,
    };
  };
  const profileForm = useForm<ModifyUserFormSchema>({
    resolver: yupResolver(editUserSchema(customInputFields)),
    defaultValues: getDefaultValues(),
  });
  const {
    formState: { isSubmitting, isDirty },
    handleSubmit,
    reset,
  } = profileForm;

  useEffect(() => {
    if (customInputFields.length > 0) {
      reset(getDefaultValues(), {
        keepDirtyValues: true,
        keepErrors: true,
        keepTouched: true,
      });
    }
  }, [customInputFields]);

  const closeModal = useCallback(() => {
    reset();
    setOpen(false);
  }, []);

  const onSubmit = useCallback(
    handleSubmit(async (values: ModifyUserFormSchema) => {
      try {
        if (editUser) {
          const payload = {
            ...editUser,
            ...values,
            firstname: values.firstname.trim(),
            lastname: values.lastname.trim(),
            email: values.email.trim(),
            active: values.active === 'Active',
            roles: [
              {
                roleName: values.role,
                roleDescription:
                  values.role === 'ROLE_COMPANY_ADMIN' ? 'Role Company Admin' : 'Role Company User',
              },
            ],
          };
          const saveAsLists = ['department', 'region'];
          // Custom field values
          const customFieldsValues: Record<string, CustomFieldValueType> = {};
          const userKeys = Object.keys(user);
          customInputFields.forEach((field) => {
            if (
              Array.isArray(values[field.entryCode])
                ? values[field.entryCode].length > 0
                : values[field.entryCode]
            ) {
              if (
                userKeys.includes(field.entryCode) ||
                field.entryType?.toUpperCase() !== ConfigFieldEntryType.CUSTOM
              ) {
                payload[field.entryCode] =
                  saveAsLists.includes(field.entryCode) && field.referenceObject?.type === 'SELECT'
                    ? [values[field.entryCode]]
                    : values[field.entryCode];
              } else {
                customFieldsValues[field.entryCode] = {
                  fieldName: field.entryCode,
                  fieldValue:
                    saveAsLists.includes(field.entryCode) &&
                    field.referenceObject?.type === 'SELECT'
                      ? [values[field.entryCode]]
                      : values[field.entryCode],
                  type: field.referenceObject?.type || '',
                  configRefName: field.referenceObject?.configRefName || '',
                  label: field.referenceObject?.label || field.entryDescription,
                };
              }
            }
          });
          payload['customFields'] = customFieldsValues;

          const { data } = await UserAPI.editUser(payload);
          updateUsersQueryCache(data.companyId, data, 'update');
          toast.success('User details updated');
          closeModal();
        }
      } catch (e) {
        toast.error(catchErrorMessage(e));
      }
    }),
    [user, customInputFields, closeModal],
  );

  return {
    customInputFields,
    isCustomFieldsFetched,
    isSubmitting,
    profileForm,
    closeModal,
    onSubmit,
    isDirty,
  };
}
